import { PagingRequestValues } from 'modules/organization/organizationApiTypes';

import { api } from 'services/api';

import { DSP_ADFORM, DSP_FREEWHEEL, DSP_XANDR } from '../../utils/constants';
import {
  ALL_TOUCH_URL,
  ANALYTICS_DOWNLOAD_URL,
  ANALYTICS_URL,
  BUDGET_URL,
  CHART_URL,
  CREATIVE_NAMES_URL,
  CREATIVE_URL,
  ADFORM_ALL_TOUCH_URL,
  ADFORM_HALO_URL,
  ADFORM_INCREMENTALITY_URL,
  FREEWHEEL_HALO_URL,
  FREEWHEEL_INCREMENTALITY_URL,
  DSP_ANALYTICS_DOWNLOAD_URL,
  DSP_ANALYTICS_URL,
  DSP_BUDGET_URL,
  DSP_CHART_URL,
  DSP_NESTED_ANALYTICS_URL,
  DSP_WIDGET_URL,
  HALO_URL,
  INCREMENTALITY_URL,
  NESTED_ANALYTICS_URL,
  PUBLISHER_URL,
  VIEW_URL,
  WIDGET_URL,
} from './constants';
import {
  AllTouchRequest,
  AllTouchResponse,
  AnalyticsRequest,
  AnalyticsResponse,
  BudgetRequest,
  BudgetResponse,
  ChartRequest,
  CreativeNamesPagingResponse,
  CreativesPagingResponse,
  DSPCreativeMetadata,
  DeleteViewRequest,
  DownloadAnalyticsRequest,
  Halo,
  HaloRequest,
  Incrementality,
  IncrementalityRequest,
  NestedAnalyticsRequest,
  PerformanceResponse,
  PublishersPagingResponse,
  View,
  WidgetRequest,
} from './performanceApiTypes';

const getViews = (advertiserId: string, dsp: string | null) => {
  const params = new URLSearchParams({ advertiserId });

  if (dsp !== null) {
    params.append('dsp', dsp);
  }

  return api.get<View[]>(`${VIEW_URL}?${params.toString()}`);
};

const saveView = (view: View, dsp: string | null) => {
  const params = dsp ? { dsp } : undefined;

  return api.post<View>(VIEW_URL, view, { params });
};

const deleteView = (request: DeleteViewRequest) => {
  return api.delete(`${VIEW_URL}/${request.viewId}`);
};

const getWidgetData = (payload: WidgetRequest) => {
  const { dsp, ...rest } = payload;

  if (dsp != DSP_XANDR) return api.post<PerformanceResponse>(`${DSP_WIDGET_URL}/${dsp}`, rest);

  return api.post<PerformanceResponse>(WIDGET_URL, rest);
};

const getBudgetData = (payload: BudgetRequest) => {
  const { dsp, ...rest } = payload;

  if (dsp != DSP_XANDR) return api.post<PerformanceResponse>(`${DSP_BUDGET_URL}/${dsp}`, rest);

  return api.post<BudgetResponse>(BUDGET_URL, rest);
};

const getNestedAnalyticsData = (payload: NestedAnalyticsRequest) => {
  const { dsp, ...rest } = payload;

  if (dsp != DSP_XANDR)
    return api.post<AnalyticsResponse>(`${DSP_NESTED_ANALYTICS_URL}/${dsp}`, rest);

  return api.post<AnalyticsResponse>(NESTED_ANALYTICS_URL, rest);
};

const getChartData = (payload: ChartRequest) => {
  const { dsp, ...rest } = payload;

  if (dsp != DSP_XANDR) return api.post<PerformanceResponse>(`${DSP_CHART_URL}/${dsp}`, rest);

  return api.post<PerformanceResponse>(CHART_URL, rest);
};

const getIncrementalityData = (payload: IncrementalityRequest, dsp?: string) => {
  if (dsp === DSP_ADFORM) return api.post<Incrementality[]>(ADFORM_INCREMENTALITY_URL, payload);
  if (dsp === DSP_FREEWHEEL)
    return api.post<Incrementality[]>(FREEWHEEL_INCREMENTALITY_URL, payload);

  return api.post<Incrementality[]>(INCREMENTALITY_URL, payload);
};

const getHaloData = (payload: HaloRequest, dsp?: string) => {
  if (dsp === DSP_ADFORM) return api.post<Halo>(ADFORM_HALO_URL, payload);
  if (dsp === DSP_FREEWHEEL) return api.post<Halo>(FREEWHEEL_HALO_URL, payload);

  return api.post<Halo>(HALO_URL, payload);
};

const getAnalyticsData = (payload: AnalyticsRequest) => {
  const { dsp, ...rest } = payload;

  if (dsp != DSP_XANDR) return api.post<AnalyticsResponse>(`${DSP_ANALYTICS_URL}/${dsp}`, rest);

  return api.post<AnalyticsResponse>(ANALYTICS_URL, rest);
};

const downloadAnalyticsData = (payload: DownloadAnalyticsRequest) => {
  const { dsp, ...rest } = payload;

  if (dsp != DSP_XANDR)
    return api.post<Blob>(`${DSP_ANALYTICS_DOWNLOAD_URL}/${dsp}`, rest, { responseType: 'blob' });

  return api.post<Blob>(ANALYTICS_DOWNLOAD_URL, rest, { responseType: 'blob' });
};

const getAllTouchData = (payload: AllTouchRequest, dsp: string) => {
  if (dsp === DSP_ADFORM) return api.post<AllTouchResponse>(`${ADFORM_ALL_TOUCH_URL}`, payload);

  return api.post<AllTouchResponse>(`${ALL_TOUCH_URL}`, payload);
};

const getPublishers = (payload: PagingRequestValues) => {
  return api.get<PublishersPagingResponse>(
    `${PUBLISHER_URL}?page=${payload.page}&size=${payload.size}`,
  );
};

const getCreatives = (payload: PagingRequestValues) => {
  return api.get<CreativesPagingResponse>(
    `${CREATIVE_URL}?page=${payload.page}&size=${payload.size}`,
  );
};

const getCreativeMetadata = (creativeId: string, dsp: string | null) => {
  if (dsp != DSP_XANDR) {
    return api.get<DSPCreativeMetadata>(`${dsp}/${CREATIVE_URL}/${creativeId}`);
  }

  return api.get<DSPCreativeMetadata>(`${CREATIVE_URL}/${creativeId}`);
};

const getCreativeNames = (payload: PagingRequestValues) => {
  return api.get<CreativeNamesPagingResponse>(
    `${CREATIVE_NAMES_URL}?page=${payload.page}&size=${payload.size}&advertiserId=${payload.advertiserId}`,
  );
};

export const performanceApi = {
  getViews,
  saveView,
  deleteView,
  getWidgetData,
  getNestedAnalyticsData,
  getChartData,
  getAnalyticsData,
  downloadAnalyticsData,
  getAllTouchData,
  getPublishers,
  getCreatives,
  getCreativeMetadata,
  getCreativeNames,
  getIncrementalityData,
  getHaloData,
  getBudgetData,
};
